import { IUser, User, Result, UserJourney, MutableChannels, ConversationRequests, ConversationMessage } from '@/classes'
import { getCookie, setCookie, deleteCookie } from '@/utils/useCookies'
import ToastsStore from '@/store/toasts'
import UsersStore from '@/store/users'
import ConversationStore from '@/store/conversations'
import WindowStore from '@/store/window'
import { readNotificationAPI } from '@/api/notifications'
import { getUserUpdatesAPI, toggleDeviceAPI, updateJourneyAPI, userFollowingGetAPI, UserUpdatesAPI } from '@/api/user'
import { getDeviceId } from '@/utils/native/useDevice'
import { notificationsTokenRefresh } from '@/utils/native/usePushNotifications'
import { muteChannelAPI } from '@/api/user'
import { authCreateAPI, authEmailValidateAPI, authPhoneValidateAPI } from '@/api/auth'
import { INotification } from '@/classes/Notification'
import * as amplitude from '@amplitude/analytics-browser'
import dayjs from 'dayjs'
import { getAppVersion } from '@/utils/native/useApp'
import * as Sentry from "@sentry/nuxt"
import { requestCreateAPI } from '~/api/requests'
import { geolocationUpdate } from '~/utils/native/useGeolocation'

export interface VersionData {
  android: string
  ios: string
  priority: 'high' | 'normal'
  description?: string
}

const store = defineStore('user', () => {
  const toastsStore = ToastsStore()
  const conversationStore = ConversationStore()
  const usersStore = UsersStore()
  const windowStore = WindowStore()

  const langExcluded: Ref<string[]> = ref([])

  const colorThemeSetting: Ref<'dark' | 'light' | 'auto'> = ref('auto')
  const colorTheme = computed(() => {
    const isDarkSystem = window && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches

    return (colorThemeSetting.value === 'auto' && isDarkSystem) || colorThemeSetting.value === 'dark' ? 'dark' : 'light'
  })

  const unreadConversationsCount = ref(0)
  const hasPartners = ref(false)
  const hasClubPartners = ref(false)

  const isSpicyUntil: Ref<Date | null> = ref(null)
  
  const followerCount = ref(0)
  const followingCount = ref(0)
  const postCount = ref(0)

  const followingOrganizations: Ref<string[]> = ref([])
  const followingPlaces: Ref<string[]> = ref([])
  const following: Ref<string[]> = ref([])
  const followers: Ref<string[]> = ref([])
  const mutuals: Ref<string[]> = ref([])
  
  const user: Ref<IUser | null> = ref(null)
  const token: Ref<string | null> = ref(null)

  const weeklyBestie: Ref<{ id: string, questions: { text: string, answers: string[] }[] } | null> = ref(null)

  const fetched = ref(false)
  const isServerError = ref(false)
  const fetchDate: Ref<Date | null> = ref(null)

  const versionServer: Ref<VersionData | null> = ref(null)
  const { version } = getAppVersion()

  const lastUpdate: Ref<Date | null> = ref(null)
  const currentUser = computed(() => {
    return user.value ? new User(user.value) : null
  })

  const currentDeviceId = ref('')
  const currentDevice = computed(() => currentUser.value?.devices?.find(device => device.id === currentDeviceId.value) ?? null)

  const notifications: Ref<INotification[]> = ref([])

  // AUTH

  const initUser = async (force = false) => {
    if ((fetchDate.value && dayjs(fetchDate.value).diff(dayjs(), 'seconds') < 60) && !force) return

    const tokenGlobal = await getCookie('token-global') as string
    token.value = tokenGlobal ? tokenGlobal : await getCookie('token') as string

    if (!token.value) return

    const response: Result<{
      updates: UserUpdatesAPI
      base: {
        version: VersionData
        weeklyBestie: {
          id: string
          questions: {
            text: string
            answers: string[]
          }[]
        }
      }  
    }> = await fetchAPI('user', { force })

    if (!response.ok) {
      isServerError.value = response.error === 'api-error'
      return
    }

    fetchDate.value = new Date()
    fetched.value = true

    // BASE DATA
    const baseData = response.data.base

    if (response.data.updates) setUpdates(response.data.updates)

    if (baseData?.version) versionServer.value = baseData.version
    if (baseData?.weeklyBestie) setWeeklyBestie(baseData.weeklyBestie)

    if (user.value) {
      identify()

      const responseFollowing = await userFollowingGetAPI()

      if (responseFollowing.ok) {
        if (responseFollowing.data.following) following.value = responseFollowing.data.following
        if (responseFollowing.data.followers) followers.value = responseFollowing.data.followers
        if (responseFollowing.data.mutuals) mutuals.value = responseFollowing.data.mutuals
        if (responseFollowing.data.followingOrganizations) followingOrganizations.value = responseFollowing.data.followingOrganizations
        if (responseFollowing.data.followingPlaces) followingPlaces.value = responseFollowing.data.followingPlaces
      }
    }
    
    isServerError.value = false

    currentDeviceId.value = await getDeviceId()

    if (currentDevice.value && dayjs(currentDevice.value.refreshedAt).diff(dayjs(), 'days') >= 3) notificationsTokenRefresh()

    return {
      ok: true
    }
  }

  const identify = async () => {    
    const identifyEvent = new amplitude.Identify()

    if (user.value) {
      amplitude.setUserId(user.value._id)
      Sentry.setUser({ id: user.value._id })

      if (!user.value?.['@profile']) return

      identifyEvent.set('email', user.value.email ?? '')
      identifyEvent.set('name', user.value.name ?? '')
      identifyEvent.set('username', user.value.username ?? '')
      identifyEvent.set('isSpicy', user.value.isSpicy ?? false)
      
      identifyEvent.set('subscriber', user.value.isSubscriber)
      identifyEvent.set('subscriber-level', user.value.entitlements[0]?.level ?? 'free')
      
      identifyEvent.set('age', user.value.age ?? 0)
      identifyEvent.set('genders', user.value['@profile'].genders ?? [])
      identifyEvent.set('flags', user.value['@profile'].flags ?? [])
      identifyEvent.set('attractions', user.value?.filters?.genders ?? [])

      identifyEvent.set('events-attended', user.value.attendedCount)
      identifyEvent.set('events-organized', user.value.organizedCount)

      if (user.value.createdAt) identifyEvent.set('createdAt', dayjs(user.value.createdAt).toISOString())
      if (user.value.registeredAt) identifyEvent.set('registeredAt', dayjs(user.value.registeredAt).toISOString())
      if (user.value.onboardedAt) identifyEvent.set('onboardedAt', dayjs(user.value.onboardedAt).toISOString())
    }

    if (version.value) identifyEvent.set('app-version', version.value)

    amplitude.identify(identifyEvent)
  }

  const create = async (
    name: string,
    language: string
  ) => {
    const response = await authCreateAPI(name, language)
    if (!response.ok) return response

    await auth(response.data.token)

    return response
  }

  const login = async (
    email: string,
    phone: string,
    code: string
  ) => {
    let response = null

    if (email) {
      response = await authEmailValidateAPI(email, code)
    } else if (phone) {
      response = await authPhoneValidateAPI(phone, code)
    }
    
    if (!response?.ok) return response
    await auth(response.data.token)
  
    return response
  }

  const register = async (
    email: string,
    phone: string,
    code: string
  ) => {
    let response = null

    if (email) {
      response = await authEmailValidateAPI(email, code)
    } else if (phone) {
      response = await authPhoneValidateAPI(phone, code)
    }

    if (!response?.ok) return response
    await auth(response.data.token)

    return response
  }

  const auth = async (token?: string | null) => {
    if (!token) return
    
    try {
      await setCookie('token', token)
      await setCookie('token-global', token, { 
        isGlobal: true
      })
      
      await setCookie('has-logged-once', 'true')
      
      await initUser(true)

      const deviceId = await getDeviceId()
      if (deviceId) await toggleDeviceAPI(deviceId, 'enable')
    } catch (e) {
      console.error(e)
    }
  }

  const logout = async () => {
    try {
      if (currentDevice.value) await toggleDeviceAPI(currentDevice.value.id, 'disable')

      user.value = null
      token.value = null
      fetchDate.value = null
      fetched.value = false

      await deleteCookie('token')
      await deleteCookie('token-global', {
        isGlobal: true
      })
    } catch (e) {
      console.error(e)
      return { errors: e }
    }

    return true
  }

  // PROFILE

  const edit = async (
    params = {},
    options?: {
      silent?: boolean
    }
  ): Promise<Result<{ success: boolean }>> => {
    const response: Result<{ updated: User, success: boolean }> = await fetchAPI('user/profile', {
      ...params
    }, { method: 'POST' })

    if (!response.ok) {
      toastsStore.error(response)
      return response
    } else if (!options?.silent) {
      toastsStore.add({ content: useNuxtApp().$i18n.t('common.editSuccess') })
    }

    if (response.data.updated) user.value = response.data.updated

    identify()

    return {
		  ok: true,
      data: {
        success: true
      }
    }
  }

  const editUsername = async (username: string): Promise<Result<{ success: boolean }>> => {
    const response: Result<{ user: User }> = await fetchAPI('user/username', { username }, { method: 'POST' })

    if (!response.ok) {
      toastsStore.error(response)
      return response
    }

    await initUser(true)

    toastsStore.add({ content: useNuxtApp().$i18n.t('common.editSuccess') })

    return {
      ok: true,
      data: {
        success: true
      }
    }
  }

  const readNotifications = async (params: {
    type: 'read' | 'delete' | 'delete-all'
    notifications?: string[]
  }) => {
    const response = await readNotificationAPI(params)

    if (response.ok && response.data?.notifications) notifications.value = response.data.notifications
  }
  
  const updateJourney = async (type: typeof UserJourney[number] | 'offer') => {
    const response = await updateJourneyAPI(type)
    if (response.ok && response.data?.user) user.value = new User(response.data.user)

    amplitude.logEvent('User_journey', { type })

    return response
  }

  const muteChannel = async (
    _id: string | undefined,
    type: typeof MutableChannels[number],
	  mute: 'hours' | 'week' | 'indefinite' | 'unmute',
    value?: string,
    token?: string
  ) => {
    const response = await muteChannelAPI({ _id, value, type, mute, token })
    if (response.ok && response.data?.user) user.value = new User(response.data.user)
  }

  const getUpdates = async (
    isActive: boolean,
    options?: {
      isGeolocate?: boolean
    }
  ) => {
    await Promise.all([
      (async () => {
        if (isActive && currentUser.value && options?.isGeolocate) await geolocationUpdate(currentUser.value)
      })(),
      (async () => {
        const response = await getUserUpdatesAPI(isActive)
        if (response.ok && response.data) setUpdates(response.data)
      })()
    ])
  }

  const setUpdates = async (data: UserUpdatesAPI) => {
    if (data) {
      if (data.user) {
        user.value = data.user
        usersStore.setItem(data.user)
      }
      
      unreadConversationsCount.value = data.conversationsCount ?? 0
      hasPartners.value = data.hasPartners
      hasClubPartners.value = data.hasClubPartners
      
      if (data.notifications) {
        notifications.value = data.notifications
      }

      if (data.followerCount) followerCount.value = data.followerCount
      if (data.followingCount) followingCount.value = data.followingCount
      if (data.postCount) postCount.value = data.postCount

      if (data.messages) {
        for (const message of data.messages.map(c => new ConversationMessage(c))) {
          const elapsed = lastUpdate.value ? dayjs().diff(lastUpdate.value, 'seconds') : 0
          const isAfter = dayjs(message.createdAt).add(1, 'seconds').isAfter(dayjs(lastUpdate.value)) 
          
          if (lastUpdate.value && elapsed < 20 && isAfter && !(user.value?.mutedChannels ?? []).find(channel => channel._id === message.conversation) && !message.target)  {
            windowStore.playSound('message_received')

            toastsStore.add({
              content: '',
              message: message,
              conversation: message.conversation
            })

            await timeout(1000)
          }
        }
      }
    }

    lastUpdate.value = new Date()
  }

  const requestCreate = async (
    targetId: string,
    type: typeof ConversationRequests[number],
    options?: {
      message?: string
      isRewarded?: boolean
    }
  ) => {
    const response = await requestCreateAPI(targetId, type, options)

    if (!response.ok) {
      toastsStore.error(response)
      return { ok: false }
    }

    if (response.data.conversation) conversationStore.setItem(response.data.conversation)
    if (response.data.requests !== undefined && user.value) user.value.requests = response.data.requests ?? 0

    toastsStore.add({
      type: 'success',
      content: useNuxtApp().$i18n.t('components.User.requests.sent')
    })

    return {
      conversationId: response.data.conversation?._id,
    }
  }
  
  const setWeeklyBestie = (wb: any) => {
    weeklyBestie.value = wb
  }

  const setSpicy = () => {
    isSpicyUntil.value = dayjs().add(30, 'minutes').toDate()
  }

  watch(user, () => {
    if (!user.value) fetched.value = false
    identify()
  }, { immediate: true})

  return {
    user, fetched, isServerError, token, currentUser, edit, readNotifications, updateJourney, currentDevice, muteChannel, getUpdates, requestCreate, setWeeklyBestie, weeklyBestie, identify,
    initUser, logout, editUsername, unreadConversationsCount, login, hasPartners, hasClubPartners, versionServer, notifications, followingCount, postCount, followerCount, following, colorTheme, colorThemeSetting, create, followers, register, followingOrganizations, followingPlaces, setSpicy, isSpicyUntil, langExcluded, mutuals
  }
})

export default store